import { useState } from 'react';
import { addEditHistory } from '@/store';
import { showToast } from '@/components/base/Toast';
import { formatCompactCurrency, formatDate } from '@/utils/format';
import type { Debt } from '@/types';

interface DebtReminderModalProps {
  open: boolean;
  onClose: () => void;
  debt: Debt;
  farmerName: string;
  htxName?: string;
}

function buildDefaultMessage(debt: Debt, farmerName: string): string {
  return `Khoản vay ${debt.maKhoanVay || debt.id} đã quá hạn (hạn trả: ${formatDate(debt.ngayDenHan)}). Số tiền còn nợ: ${formatCompactCurrency(debt.conNo)}. Vui lòng thanh toán theo QR hoặc liên hệ HTX để được hỗ trợ.`;
}

export function DebtReminderModal({ open, onClose, debt, farmerName, htxName }: DebtReminderModalProps) {
  const defaultMsg = buildDefaultMessage(debt, farmerName);
  const [message, setMessage] = useState(defaultMsg);
  const [sending, setSending] = useState(false);

  if (!open) return null;

  const handleSend = () => {
    setSending(true);

    const now = new Date().toISOString();
    const sentByName = htxName || 'HTX';

    // NOTE: the real backend has no "create an arbitrary in-app notification"
    // endpoint (notify.controller.ts only lists/marks-read the current user's
    // own) — an HTX-initiated reminder can't be persisted into the farmer's
    // notification list from here. The activity log below and the browser
    // notification are the only durable/immediate feedback for this action.

    // Activity log
    addEditHistory({
      entityType: 'Debt',
      entityId: debt.id,
      farmerId: debt.farmerId,
      truong: 'reminder_sent',
      giaTriCu: '',
      giaTriMoi: message.trim(),
      ngaySua: now,
      nguoiSua: sentByName,
    });

    // Browser notification (if permission granted)
    try {
      if ('Notification' in window && Notification.permission === 'granted') {
        new Notification(`Nhắc trả nợ - ${farmerName}`, {
          body: `Khoản vay ${debt.maKhoanVay || debt.id}: ${formatCompactCurrency(debt.conNo)}`,
          icon: '/favicon.ico',
        });
      }
    } catch {
      // silently ignore browser notification errors
    }

    showToast(`Đã gửi nhắc trả cho ${farmerName}`, 'success');

    setSending(false);
    onClose();
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div
      className="fixed inset-0 z-[90] flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={handleOverlayClick}
    >
      <div className="absolute inset-0 bg-black/40" />
      <div className="relative bg-white rounded-t-2xl sm:rounded-2xl shadow-xl w-full sm:max-w-md max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-amber-50 border-b border-amber-100 px-6 py-4 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-amber-800 flex items-center gap-2">
              <span className="w-6 h-6 flex items-center justify-center">
                <i className="ri-notification-3-line" />
              </span>
              Gửi nhắc trả nợ
            </h3>
            <p className="text-xs text-amber-600 mt-0.5">
              {farmerName} · {debt.maKhoanVay || debt.id}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-amber-100 text-amber-600"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-4 space-y-4">
          {/* Debt summary */}
          <div className="bg-slate-50 rounded-xl p-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-slate-500">Khoản vay</span>
              <span className="font-medium text-slate-900">{debt.maKhoanVay || debt.id}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-slate-500">Số tiền còn nợ</span>
              <span className="font-bold text-red-700">{formatCompactCurrency(debt.conNo)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-slate-500">Ngày đến hạn</span>
              <span className="font-medium text-slate-900">{formatDate(debt.ngayDenHan)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-slate-500">Trạng thái</span>
              <span className="text-[11px] px-2 py-0.5 rounded-full font-medium bg-red-100 text-red-700">
                {debt.status}
              </span>
            </div>
          </div>

          {/* Editable message */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              Nội dung thông báo
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              maxLength={500}
              rows={4}
              className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 resize-none"
              placeholder="Nhập nội dung nhắc trả..."
            />
            <div className="flex justify-between mt-1">
              <p className="text-[10px] text-slate-400">
                Bạn có thể chỉnh sửa nội dung trước khi gửi
              </p>
              <p className="text-[10px] text-slate-400">
                {message.length}/500
              </p>
            </div>
          </div>

          {/* Info */}
          <div className="bg-sky-50 border border-sky-200 rounded-xl p-3">
            <p className="text-xs text-sky-700 flex items-center gap-1.5">
              <span className="w-4 h-4 flex items-center justify-center shrink-0">
                <i className="ri-information-line" />
              </span>
              Hành động này được ghi lại vào lịch sử hoạt động. Nếu trình duyệt hỗ trợ, một browser notification cũng sẽ hiển thị.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white border-t border-slate-100 px-6 py-4 flex gap-3">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-4 py-2.5 rounded-xl text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 transition-colors"
          >
            Hủy
          </button>
          <button
            type="button"
            onClick={handleSend}
            disabled={sending || !message.trim()}
            className="flex-1 px-4 py-2.5 rounded-xl text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 disabled:bg-amber-300 transition-colors flex items-center justify-center gap-1.5 whitespace-nowrap"
          >
            {sending ? (
              <>
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-loader-4-line animate-spin" />
                </span>
                Đang gửi...
              </>
            ) : (
              <>
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-send-plane-line" />
                </span>
                Gửi thông báo
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}